import type { Deal } from "../types";
import { calc } from "../engine/engine";
import { money0, pct, x2 } from "../lib/format";

export function Portfolio({
  deals,
  onOpen,
  onNew,
}: {
  deals: Deal[];
  onOpen: (id: string) => void;
  onNew: () => void;
}) {
  const acquisitionDeals = deals.filter((d) => d.kind !== "land-development");
  const rows = deals.map((d) => ({ d, m: calc(d) }));
  const avg = Math.round(
    acquisitionDeals.reduce((sum, d) => sum + calc(d).score.composite, 0) / Math.max(1, acquisitionDeals.length)
  );
  const totNOI = acquisitionDeals.reduce((sum, d) => sum + calc(d).noi, 0);
  const totVal = acquisitionDeals.reduce((sum, d) => sum + calc(d).stabVal, 0);

  return (
    <div className="wrap">
      <div className="grid g4">
        <Kpi label="Properties" value={String(deals.length)} sub="in this workspace" />
        <Kpi label="Avg composite score" value={String(avg)} sub="across pipeline" />
        <Kpi label="Combined NOI" value={money0(totNOI)} sub="annual, stabilized" />
        <Kpi label="Combined value" value={money0(totVal)} sub="stabilized est." />
      </div>

      <div className="pe">
        <div className="l">◆ In plain English</div>
        <p>
          Your whole book at a glance — each card is one property with its live
          score. Click any card to open its full deal file; change any number
          inside and every metric recomputes instantly.
        </p>
      </div>

      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 12 }}>
        <button className="btn btn-a btn-sm" onClick={onNew}>
          ＋ New deal
        </button>
      </div>

      <div className="grid g3">
        {rows.map(({ d, m }) => {
          if (d.kind === "land-development" && d.development) {
            const p = d.development;
            return (
              <div key={d.id} className="card deal-card" onClick={() => onOpen(d.id)}>
                <div style={{ padding: "15px 16px", display: "flex", justifyContent: "space-between", gap: 10 }}>
                  <div>
                    <div className="fr" style={{ fontWeight: 600, fontSize: 17, color: "var(--forest)" }}>{d.name}</div>
                    <div style={{ fontSize: 11.5, color: "var(--muted)", marginTop: 2 }}>{d.addr}</div>
                    <div style={{ marginTop: 8, display: "flex", gap: 6, flexWrap: "wrap" }}>
                      <span className="tag t-watch">FEASIBILITY</span>
                      <span className="tag" style={{ background: "var(--cream-200)", color: "var(--forest)" }}>{d.stage}</span>
                    </div>
                  </div>
                  <div style={{ textAlign: "center" }}>
                    <div className="fr" style={{ fontSize: 28, fontWeight: 600, color: "var(--forest)", lineHeight: 1 }}>{p.grossAcres.toFixed(1)}</div>
                    <div style={{ fontSize: 9, letterSpacing: ".1em", color: "var(--muted)" }}>ACRES</div>
                  </div>
                </div>
                <div style={{ padding: "0 16px 12px", display: "flex", gap: 14, fontSize: 11.5, color: "var(--muted)" }}>
                  <span>Base <b className="mono" style={{ color: "var(--forest)" }}>{p.scenarios[0]?.units} units</b></span>
                  <span>Folio <b className="mono" style={{ color: "var(--forest)" }}>{p.folio}</b></span>
                </div>
                <div className="barw"><span style={{ width: "100%", background: "var(--brass)" }} /></div>
              </div>
            );
          }
          const s = m.score;
          const barColor =
            s.cls === "fail" ? "var(--fail)" : s.cls === "watch" ? "var(--brass)" : "var(--pass)";
          return (
            <div key={d.id} className="card deal-card" onClick={() => onOpen(d.id)}>
              <div style={{ padding: "15px 16px", display: "flex", justifyContent: "space-between", gap: 10 }}>
                <div>
                  <div className="fr" style={{ fontWeight: 600, fontSize: 17, color: "var(--forest)" }}>{d.name}</div>
                  <div style={{ fontSize: 11.5, color: "var(--muted)", marginTop: 2 }}>{d.addr || d.market}</div>
                  <div style={{ marginTop: 8, display: "flex", gap: 6, flexWrap: "wrap" }}>
                    <span className={"tag t-" + (s.cls === "pass" ? "pass" : s.cls === "watch" ? "watch" : "fail")}>{s.band}</span>
                    <span className="tag" style={{ background: "var(--cream-200)", color: "var(--forest)" }}>{d.stage}</span>
                  </div>
                </div>
                <div style={{ textAlign: "center" }}>
                  <div className="fr" style={{ fontSize: 30, fontWeight: 600, color: "var(--forest)", lineHeight: 1 }}>
                    {s.gated ? "—" : s.composite}
                  </div>
                  <div style={{ fontSize: 9, letterSpacing: ".1em", color: "var(--muted)" }}>SCORE</div>
                </div>
              </div>
              <div style={{ padding: "0 16px 12px", display: "flex", gap: 14, fontSize: 11.5, color: "var(--muted)" }}>
                <span>DSCR <b className="mono" style={{ color: "var(--forest)" }}>{x2(m.dscr)}</b></span>
                <span>Cap <b className="mono" style={{ color: "var(--forest)" }}>{pct(m.capOnCost)}</b></span>
                <span>Units <b className="mono" style={{ color: "var(--forest)" }}>{d.u2c + d.u1c}</b></span>
              </div>
              <div className="barw"><span style={{ width: (s.gated ? 100 : s.composite) + "%", background: barColor }} /></div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Kpi({ label, value, sub }: { label: string; value: string; sub: string }) {
  return (
    <div className="card kpi">
      <div className="kl">{label}</div>
      <div className="kv">{value}</div>
      <div className="ks">{sub}</div>
    </div>
  );
}
